export default class Audio {
	static create(chapter) {
		const audioPlayerElement = chapter.container.querySelector('[data-chapter-audio-player]');

		if (!audioPlayerElement) {
			return null;
		}

		return new Audio(audioPlayerElement, chapter);
	}

	constructor(el, chapter) {
		this.chapter = chapter;

		// DOM elements
		this.el = el;
		this.audioElement = this.el.querySelector('[data-chapter-audio]');
		this.scrollProgressElement = this.el.querySelector('[data-scroll-progress]');

		this.audioProgress = this.el.querySelector('[data-audio-progress]');
		this.audioProgressBar = this.el.querySelector('[data-audio-progress-bar]');
		this.audioProgressTime = this.el.querySelector('[data-audio-progress-time]');
		this.volumeBarElement = this.el.querySelector('[data-volume-bar]');
		this.muteButton = this.el.querySelector('[data-mute]');
		this.uiParts = this.el.querySelectorAll('[data-ui-part]');

		// Volume
		this.volume = this.audioElement.volume;

		// Initialize subtitles
		this.createSubtitles();

		// UI
		this.playToggle = this.el.querySelector('[data-play-button]');
		this.progressBar = this.el.querySelector('[data-progress-bar]');

		// UI interaction
		this.el.addEventListener('click', e => {
			if (!e.target) {
				return;
			}

			const button = e.target.closest('[data-ui-part-button]');
			if (!button) {
				return;
			}

			const index = parseInt(button.dataset.uiPartButton, 10);
			this.chapter.goToPart(index);
		});

		this.playToggle.addEventListener('click', e => {
			this.audioElement.paused ? this.play() : this.pause();
		});

		this.progressBar.addEventListener('mousedown', () => {
			this.mouseDownOnSlider = true;
		});

		this.progressBar.addEventListener('mouseup', () => {
			this.mouseDownOnSlider = false;
		});

		this.volumeBarElement.addEventListener('input', e => {
			if (e.target.value == 0) {
				this.mute();
			} else {
				this.audioElement.volume = parseFloat(e.target.value);
				this.volume = this.audioElement.volume;
				this.prevVolume = null;
				this.unMute();
			}
		});

		// Audio events
		this.audioElement.addEventListener('play', e => {
			this.el.classList.add('is-playing');
		});

		this.audioElement.addEventListener('pause', e => {
			this.el.classList.remove('is-playing');
		});

		this.audioElement.addEventListener('ended', e => {
			this.el.classList.remove('is-playing');
		});

		this.audioElement.addEventListener('timeupdate', () => {
			if (this.mouseDownOnSlider) {
				return;
			}

			const progress = (this.audioElement.currentTime / this.audioElement.duration) * 100;
			if(isNaN(progress)) {
				return;
			}

			this.progressBar.value = progress;
			this.audioProgressTime.innerText = this.formatTime(this.audioElement.currentTime);
			this.audioProgress.style.setProperty('--audio-progress', `${progress}%`);
		});

		this.progressBar.addEventListener('change', () => {
			const percentage = this.progressBar.value / 100;
			this.audioElement.currentTime = (this.audioElement.duration || 0) * percentage;
		});

		this.muteButton.addEventListener('click', () => {
			if (this.audioElement.muted) {
				this.unMute();
			} else {
				this.mute();
			}
		});
	}

	moveProgressBar(index) {
		this.audioProgressTime.innerText = '00:00';
		this.audioProgress.style.setProperty('--audio-progress', '0%');

		const currentProgressWrapper = this.el.querySelector('[data-ui-part].is-active [data-progress-wrapper]');
		this.uiParts[index].querySelector('[data-progress-wrapper]').append(...currentProgressWrapper.childNodes);
		this.uiParts.forEach(uiPart => uiPart.classList.remove('is-active'));
		this.uiParts[index].classList.add('is-active');
	}

	formatTime(time) {
		let minutes = Math.floor(time / 60);
		minutes = minutes >= 10 ? minutes : '0' + minutes;
		let seconds = Math.floor(time % 60);
		seconds = seconds >= 10 ? seconds : '0' + seconds;
		return minutes + ':' + seconds;
	}

	activateSubtitles(index) {
		this.subtitles.forEach((part, _index) => {
			if (index == _index) {
				part.el.classList.add('is-active');
			} else {
				part.el.classList.remove('is-active');
			}

			part.subtitles.forEach(subtitle => subtitle.el.classList.remove('is-active'));
		});
	}

	createSubtitles() {
		this.subtitles = Array.from(this.el.querySelectorAll('[data-chapter-parts-subs]')).map(chapterPartSubtitles => {
			return {
				el: chapterPartSubtitles,
				subtitles: Array.from(chapterPartSubtitles.querySelectorAll('[data-chapter-part-sub]')).map(subtitle => {
					return {
						el: subtitle,
						start: parseFloat(subtitle.dataset.start),
						end: parseFloat(subtitle.dataset.end),
					};
				}),
			};
		});

		this.audioElement.addEventListener('timeupdate', () => {
			const currentTime = this.audioElement.currentTime;

			this.subtitles[this.chapter.currentChapterPartIndex].subtitles.forEach(subtitle => {
				if (currentTime >= subtitle.start && currentTime < subtitle.end) {
					subtitle.el.classList.add('is-active');
				} else {
					subtitle.el.classList.remove('is-active');
				}
			});
		});

		this.activateSubtitles(this.chapter.currentChapterPartIndex);
	}

	get active() {
		return this.el.classList.contains('is-active');
	}

	/**
	 * @param {any} src
	 */
	set src(src) {
		if (!this.audioElement) {
			return;
		}

		this.audioElement.src = src;
	}

	loadChapterParts() {
		return Promise.all(
			this.chapter.parts.map(async part => {
				if (part.data.audio_url) {
					const objectUrl = await this.loadSource(part.data.audio_url);
					part.data.object_url = objectUrl;
				}
			}),
		);
	}

	async loadSource(src) {
		// const res = await fetch(src);
		// const blob = await res.blob();
		// return URL.createObjectURL(blob);

		let response = await fetch(src);
		const reader = response.body.getReader();
		const contentLength = +response.headers.get('Content-Length');
		let receivedLength = 0;
		let chunks = [];

		while (true) {
			const { done, value } = await reader.read();

			if (done) {
				break;
			}

			chunks.push(value);
			receivedLength += value.length;
		}

		return URL.createObjectURL(new Blob(chunks, { type: 'audio/mpeg' }));
	}

	fadeIn(duration = 2000) {
		if (this.fadeOutTimer) {
			clearInterval(this.fadeOutTimer);
		}

		this.fadeInTimer = setInterval(() => {
			if (this.audioElement.volume >= this.volume) {
				clearInterval(this.fadeInTimer);
				this.fadeInTimer = null;
				return;
			}
			this.audioElement.volume = Math.min(1, this.audioElement.volume + 0.05);
		}, duration / 20);
	}

	fadeOut(duration = 2000) {
		if (this.fadeInTimer) {
			clearInterval(this.fadeInTimer);
		}

		this.setVolume();

		this.fadeOutTimer = setInterval(() => {
			if (this.audioElement.volume <= 0) {
				clearInterval(this.fadeOutTimer);
				this.fadeOutTimer = null;
				this.pause();
				return;
			}

			this.audioElement.volume = Math.max(0, this.audioElement.volume - 0.05);
		}, duration / 20);
	}

	mute() {
		this.prevVolume = this.audioElement.volume;
		this.audioElement.muted = true;
		this.el.classList.add('is-muted');
		this.volumeBarElement.value = 0;
	}

	unMute() {
		this.audioElement.muted = false;
		this.el.classList.remove('is-muted');
		if (this.prevVolume) {
			this.volumeBarElement.value = this.prevVolume;
		}
	}

	setVolume(volume = null) {
		if (volume) {
			this.audioElement.volume = volume;
		}

		this.volume = this.audioElement.volume;
	}

	play() {
		return this.audioElement.play();
	}

	pause() {
		if (!this.audioElement) {
			return;
		}

		this.audioElement.pause();
	}

	destroy() {
		clearInterval(this.fadeInTimer);
		clearInterval(this.fadeOutTimer);
		this.pause();
		this.audioElement.remove();
	}
}
